/**
 * CMSPageView - Public page renderer for CMS pages
 */
import React from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { cmsAPI, churchesAPI } from '@/lib/api';
import { PublicLayout } from '@/components/layouts';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { 
  Church, MapPin, Phone, Mail, ExternalLink, Star, Quote,
  Target, Eye, Heart, Zap, Users, Shield
} from 'lucide-react';

// Icon mapping for cards
const ICON_MAP = {
  star: Star,
  heart: Heart,
  zap: Zap,
  target: Target,
  eye: Eye,
  users: Users,
  shield: Shield,
  church: Church
};

// ============== Block Renderers ==============

const HeroBlock = ({ content }) => {
  const bgStyle = {};
  
  if (content.background_type === 'gradient') {
    bgStyle.background = `linear-gradient(to right, ${content.gradient_from || '#3B82F6'}, ${content.gradient_to || '#6366F1'})`;
  } else if (content.background_type === 'color') {
    bgStyle.backgroundColor = content.background_color || '#3B82F6';
  } else if (content.background_type === 'image' && content.image_url) {
    bgStyle.backgroundImage = `url(${content.image_url})`;
    bgStyle.backgroundSize = 'cover';
    bgStyle.backgroundPosition = 'center';
  }

  const textAlign = content.alignment || 'center';

  return (
    <div className="relative overflow-hidden" style={bgStyle}>
      {content.background_type === 'image' && (
        <div className="absolute inset-0 bg-black/40" />
      )}
      <div className={`relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 text-${textAlign}`}>
        <h1 
          className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-4"
          style={{ color: content.text_color || '#FFFFFF' }}
        >
          {content.title}
        </h1>
        {content.subtitle && (
          <p 
            className="text-xl sm:text-2xl max-w-3xl mx-auto opacity-90"
            style={{ color: content.text_color || '#FFFFFF' }}
          >
            {content.subtitle}
          </p>
        )}
        {content.cta_text && content.cta_link && (
          <Button 
            size="lg" 
            className="mt-8"
            onClick={() => {
              if (content.cta_link.startsWith('http')) {
                window.open(content.cta_link, '_blank');
              } else {
                window.location.href = content.cta_link;
              }
            }}
          >
            {content.cta_text}
          </Button>
        )}
      </div>
    </div>
  );
};

const TextBlock = ({ content }) => {
  const textAlign = content.alignment || 'left';
  
  return (
    <div className={`max-w-4xl mx-auto px-4 py-8 text-${textAlign}`}>
      {content.heading && (
        <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-4">
          {content.heading}
        </h2>
      )}
      <div className="prose prose-slate max-w-none">
        {content.content?.split('\n').map((paragraph, i) => (
          <p key={i} className="text-slate-600 mb-4">{paragraph}</p>
        ))}
      </div>
    </div>
  );
};

const ImageBlock = ({ content }) => {
  if (!content.image_url) return null;
  
  const imageUrl = content.image_url.startsWith('http') 
    ? content.image_url 
    : `${process.env.REACT_APP_BACKEND_URL}${content.image_url}`;

  return (
    <div className={`max-w-4xl mx-auto px-4 py-8 text-${content.alignment || 'center'}`}>
      <img 
        src={imageUrl}
        alt={content.alt_text || ''}
        className={`${content.width === 'full' ? 'w-full' : 'max-w-2xl mx-auto'} rounded-lg shadow-lg`}
      />
      {content.caption && (
        <p className="text-sm text-slate-500 mt-3">{content.caption}</p>
      )}
    </div>
  );
};

const CardsBlock = ({ content }) => {
  const columns = content.columns || 3;
  const gridCols = columns === 2 ? 'md:grid-cols-2' : columns === 4 ? 'md:grid-cols-4' : 'md:grid-cols-3';

  return (
    <div className="max-w-7xl mx-auto px-4 py-12">
      <div className={`grid grid-cols-1 ${gridCols} gap-6`}>
        {content.cards?.map((card, i) => {
          const Icon = ICON_MAP[card.icon] || Star;
          return (
            <Card key={i} className="text-center">
              <CardContent className="pt-6">
                <div className="w-14 h-14 bg-blue-100 rounded-xl flex items-center justify-center mx-auto mb-4">
                  <Icon className="h-7 w-7 text-blue-600" />
                </div>
                <h3 className="font-semibold text-lg text-slate-900 mb-2">{card.title}</h3>
                <p className="text-sm text-slate-600">{card.description}</p>
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
};

const CTABlock = ({ content }) => {
  return (
    <div 
      className="py-16"
      style={{ backgroundColor: content.background_color || '#F3F4F6' }}
    >
      <div className="max-w-3xl mx-auto px-4 text-center">
        <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-4">
          {content.heading}
        </h2>
        {content.description && (
          <p className="text-lg text-slate-600 mb-8">{content.description}</p>
        )}
        {content.button_text && (
          <Button 
            size="lg"
            onClick={() => {
              if (content.button_link?.startsWith('http')) {
                window.open(content.button_link, '_blank');
              } else {
                window.location.href = content.button_link || '#';
              }
            }}
          >
            {content.button_text}
          </Button>
        )}
      </div>
    </div>
  );
};

const DividerBlock = ({ content }) => {
  const style = content.style || 'line';
  const borderStyle = style === 'dashed' ? 'dashed' : style === 'dotted' ? 'dotted' : 'solid';
  
  return (
    <div className="max-w-4xl mx-auto px-4 py-4">
      <hr 
        className="border-t-2"
        style={{ 
          borderColor: content.color || '#E5E7EB',
          borderStyle
        }}
      />
    </div>
  );
};

const SpacerBlock = ({ content }) => {
  const heights = { sm: '1rem', md: '2rem', lg: '4rem', xl: '6rem' };
  const height = heights[content.height] || heights.md;
  
  return <div style={{ height }} />;
};

const AccordionBlock = ({ content }) => {
  return (
    <div className="max-w-3xl mx-auto px-4 py-8">
      <Accordion type={content.allow_multiple ? 'multiple' : 'single'} collapsible>
        {content.items?.map((item, i) => (
          <AccordionItem key={i} value={`item-${i}`}>
            <AccordionTrigger className="text-left font-medium">
              {item.question}
            </AccordionTrigger>
            <AccordionContent className="text-slate-600">
              {item.answer}
            </AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>
    </div>
  );
};

const TestimonialsBlock = ({ content }) => {
  return (
    <div className="max-w-7xl mx-auto px-4 py-12">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {content.testimonials?.map((testimonial, i) => (
          <Card key={i}>
            <CardContent className="pt-6">
              <Quote className="h-8 w-8 text-blue-200 mb-4" />
              <p className="text-slate-600 italic mb-4">"{testimonial.quote}"</p>
              <div className="flex items-center gap-3">
                {testimonial.avatar_url ? (
                  <img 
                    src={testimonial.avatar_url}
                    alt={testimonial.author}
                    className="h-10 w-10 rounded-full object-cover"
                  />
                ) : (
                  <div className="h-10 w-10 bg-slate-100 rounded-full flex items-center justify-center">
                    <span className="text-lg font-medium text-slate-500">
                      {testimonial.author?.[0]}
                    </span>
                  </div>
                )}
                <div>
                  <p className="font-medium text-slate-900">{testimonial.author}</p>
                  {testimonial.role && (
                    <p className="text-sm text-slate-500">{testimonial.role}</p>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
};

const ContactInfoBlock = ({ content }) => {
  return (
    <div className="max-w-2xl mx-auto px-4 py-12">
      <div className="space-y-4">
        {content.email && (
          <div className="flex items-center gap-3 text-slate-600">
            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
              <Mail className="h-5 w-5 text-blue-600" />
            </div>
            <a href={`mailto:${content.email}`} className="hover:text-blue-600">
              {content.email}
            </a>
          </div>
        )}
        {content.phone && (
          <div className="flex items-center gap-3 text-slate-600">
            <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
              <Phone className="h-5 w-5 text-green-600" />
            </div>
            <a href={`tel:${content.phone}`} className="hover:text-green-600">
              {content.phone}
            </a>
          </div>
        )}
        {content.address && (
          <div className="flex items-start gap-3 text-slate-600">
            <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center shrink-0">
              <MapPin className="h-5 w-5 text-purple-600" />
            </div>
            <span>{content.address}</span>
          </div>
        )}
      </div>
      {content.show_map && content.map_embed_url && (
        <div className="mt-8 rounded-lg overflow-hidden border">
          <iframe 
            src={content.map_embed_url}
            width="100%"
            height="300"
            style={{ border: 0 }}
            allowFullScreen
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
          />
        </div>
      )}
    </div>
  );
};

const ChurchesListBlock = ({ content }) => {
  const { data: churches, isLoading } = useQuery({
    queryKey: ['cms-churches', content.show_global_only],
    queryFn: () => churchesAPI.directory().then(r => r.data)
  });

  if (isLoading) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {[1, 2, 3].map(i => <Skeleton key={i} className="h-48" />)}
        </div>
      </div>
    );
  }

  const allChurches = content.show_global_only 
    ? churches?.global_churches || []
    : [...(churches?.global_churches || []), ...(churches?.other_churches || [])];

  const columns = content.columns || 3;
  const gridCols = columns === 2 ? 'md:grid-cols-2' : columns === 4 ? 'md:grid-cols-4' : 'md:grid-cols-3';

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      <div className={`grid grid-cols-1 ${gridCols} gap-6`}>
        {allChurches.map(church => {
          const logoUrl = church.logo_url 
            ? (church.logo_url.startsWith('http') ? church.logo_url : `${process.env.REACT_APP_BACKEND_URL}${church.logo_url}`)
            : null;

          return (
            <Card key={church.id} className="hover:shadow-md transition-shadow">
              <CardContent className="pt-6">
                <div className="flex items-start gap-4">
                  {content.show_logo && (
                    logoUrl ? (
                      <img src={logoUrl} alt={church.name} className="h-12 w-12 object-contain rounded" />
                    ) : (
                      <div className="h-12 w-12 bg-slate-100 rounded flex items-center justify-center">
                        <Church className="h-6 w-6 text-slate-400" />
                      </div>
                    )
                  )}
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-slate-900">{church.name}</h3>
                    {content.show_location && church.location && (
                      <p className="text-sm text-slate-500 flex items-center gap-1 mt-1">
                        <MapPin className="h-3 w-3" />
                        {church.location}
                      </p>
                    )}
                    {content.show_contact && church.contact_phone && (
                      <p className="text-sm text-slate-500 flex items-center gap-1 mt-1">
                        <Phone className="h-3 w-3" />
                        {church.contact_phone}
                      </p>
                    )}
                  </div>
                </div>
                {church.website_url && (
                  <Button 
                    variant="outline" 
                    size="sm"
                    className="w-full mt-4"
                    onClick={() => window.open(church.website_url, '_blank')}
                  >
                    <ExternalLink className="h-3 w-3 mr-1" />
                    Visit
                  </Button>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
};

const EmbedBlock = ({ content }) => {
  if (!content.html) return null;

  if (content.is_iframe) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div 
          className="rounded-lg overflow-hidden"
          dangerouslySetInnerHTML={{ __html: content.html }}
        />
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      <div dangerouslySetInnerHTML={{ __html: content.html }} />
    </div>
  );
};

// Block renderer dispatcher
const BlockRenderer = ({ block }) => {
  switch (block.type) {
    case 'hero':
      return <HeroBlock content={block.content} />;
    case 'text':
      return <TextBlock content={block.content} />;
    case 'image':
      return <ImageBlock content={block.content} />;
    case 'cards':
      return <CardsBlock content={block.content} />;
    case 'cta':
      return <CTABlock content={block.content} />;
    case 'divider':
      return <DividerBlock content={block.content} />;
    case 'spacer':
      return <SpacerBlock content={block.content} />;
    case 'accordion':
      return <AccordionBlock content={block.content} />;
    case 'testimonials':
      return <TestimonialsBlock content={block.content} />;
    case 'contact_info':
      return <ContactInfoBlock content={block.content} />;
    case 'churches_list':
      return <ChurchesListBlock content={block.content} />;
    case 'embed':
      return <EmbedBlock content={block.content} />;
    default:
      return null;
  }
};

// ============== Main Component ==============

const CMSPageView = () => {
  const { slug } = useParams();

  const { data: page, isLoading, error } = useQuery({
    queryKey: ['cms-page', slug],
    queryFn: () => cmsAPI.getPage(slug).then(r => r.data),
    enabled: !!slug
  });

  // Update document title - must be before any conditional returns
  React.useEffect(() => {
    if (page?.meta_title || page?.title) {
      document.title = page.meta_title || page.title;
    }
  }, [page]);

  if (isLoading) {
    return (
      <PublicLayout>
        <div className="min-h-screen">
          <Skeleton className="h-64 w-full" />
          <div className="max-w-4xl mx-auto px-4 py-8 space-y-4">
            <Skeleton className="h-8 w-64" />
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-3/4" />
          </div>
        </div>
      </PublicLayout>
    );
  }

  if (error || !page) {
    return (
      <PublicLayout>
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-center">
            <h1 className="text-4xl font-bold text-slate-900 mb-4">Page Not Found</h1>
            <p className="text-slate-600 mb-8">The page you're looking for doesn't exist.</p>
            <Button onClick={() => window.location.href = '/'}>
              Go Home
            </Button>
          </div>
        </div>
      </PublicLayout>
    );
  }

  return (
    <PublicLayout 
      hideHeader={page.header_style === 'hidden'}
      hideFooter={page.footer_style === 'hidden'}
    >
      {/* Render blocks */}
      {page.blocks?.map((block, index) => (
        <BlockRenderer key={block.id || index} block={block} />
      ))}

      {/* Empty state */}
      {(!page.blocks || page.blocks.length === 0) && (
        <div className="min-h-[50vh] flex items-center justify-center">
          <div className="text-center text-slate-500">
            <h2 className="text-2xl font-semibold mb-2">{page.title}</h2>
            <p>This page has no content yet.</p>
          </div>
        </div>
      )}
    </PublicLayout>
  );
};

export default CMSPageView;
